import { useQuery } from '@tanstack/react-query';
import { administrationClient, unwrap, createResource } from '@/shared/api-client';
import { listUsers } from '@/modules/users/api';

export interface Employee {
  id: string;
  name: string;
  userId: string | null;
  userLabel: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeePage {
  items: Employee[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateEmployeeInput {
  name: string;
  userId?: string | null;
}

export interface UpdateEmployeeInput {
  name?: string;
  userId?: string | null;
  active?: boolean;
}

const usersForLinkKey = ['employees', 'users'] as const;

export const listEmployees = (): Promise<EmployeePage> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/employees'),
  ) as Promise<EmployeePage>;

export const createEmployee = (input: CreateEmployeeInput): Promise<Employee> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/employees', {
      body: { name: input.name, userId: input.userId ?? undefined },
    }),
  ) as Promise<Employee>;

export const updateEmployee = (id: string, input: UpdateEmployeeInput): Promise<Employee> =>
  unwrap(
    administrationClient.PATCH('/api/v1/franchise-administration/employees/{id}', {
      params: { path: { id } },
      body: input as { name?: string; userId?: string; active?: boolean },
    }),
  ) as Promise<Employee>;

/** Employees routed through the shared resource factory (ADR 0012); the typed
 *  fetch closures stay per-module, the react-query ribbon is the factory's. */
const employees = createResource({
  key: ['employees'],
  list: listEmployees,
  create: createEmployee,
  update: ({ id, input }: { id: string; input: UpdateEmployeeInput }) => updateEmployee(id, input),
});

export const useEmployees = employees.useList;
export const useCreateEmployee = employees.useCreate;
export const useUpdateEmployee = employees.useUpdate;

export function useUsersForEmployeeLinks(enabled: boolean) {
  return useQuery({
    queryKey: usersForLinkKey,
    queryFn: () => listUsers({ pageSize: 100 }),
    enabled,
  });
}
